/**
 * Pure geometry for source-document highlight regions — shared by the PAINT
 * paths (CustomPDFViewer, ImageViewer) and the reverse-hover HIT-TEST, so the
 * two can never disagree about where a band is. No `app/` imports: this module
 * sits below the feature layer, which is what lets `app/recap` depend on it.
 */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface SourceRegion {
  flowId: string;
  page: number;
  rect: Rect;
  angle?: number;
}

/** Resting bands are NEUTRAL: status colour belongs to the hovered state, and
 *  repeating it at rest would reintroduce the muddy-blend problem for
 *  information the user has not asked for yet (design §2.4b). */
export const RESTING_FILL = "#6B72801F";
export const RESTING_OUTLINE = "#6B728059";

// ---------------------------------------------------------------- geometry

/** Bounding box of `frame` rotated by `angle` clockwise degrees, centred on
 *  `frame` — the overlay in which deskewed coordinates are laid out. */
export function deskewedOverlayBox(frame: Box, angle: number): Box {
  const rad = (angle * Math.PI) / 180;
  const width =
    frame.width * Math.abs(Math.cos(rad)) +
    frame.height * Math.abs(Math.sin(rad));
  const height =
    frame.width * Math.abs(Math.sin(rad)) +
    frame.height * Math.abs(Math.cos(rad));
  return {
    left: frame.left + (frame.width - width) / 2,
    top: frame.top + (frame.height - height) / 2,
    width,
    height,
  };
}

/** The placed, unrotated box a page's tilted highlights are laid out in:
 *  sized by the document tilt alone against the unrotated page dims (which
 *  swap at 90/270 user rotation), centred on the CANVAS — not on the page
 *  frame, which differs from the canvas at 90/270. The caller rotates it by
 *  `angle + normRotation`. Single definition shared by the paint path
 *  (CustomPDFViewer.renderAngledRows) and `flowsAtCanvasPoint`/
 *  `restingLayersForPage`, so the three can never disagree about where a
 *  tilted overlay sits. */
export function pageOverlayBox(
  angle: number,
  canvas: { width: number; height: number },
  normRotation: number,
): Box {
  const w0 = normRotation % 180 === 0 ? canvas.width : canvas.height;
  const h0 = normRotation % 180 === 0 ? canvas.height : canvas.width;
  const sized = deskewedOverlayBox(
    { left: 0, top: 0, width: w0, height: h0 },
    angle,
  );
  return {
    left: (canvas.width - sized.width) / 2,
    top: (canvas.height - sized.height) / 2,
    width: sized.width,
    height: sized.height,
  };
}

/** A pointer → PERCENT of `overlay`, inverting the `rotate(rotation)` the
 *  paint path applies to that overlay about its own centre. */
export function pointInRotatedOverlay(
  point: { x: number; y: number },
  overlay: Box,
  rotation: number,
): { x: number; y: number } {
  const rad = (-rotation * Math.PI) / 180;
  const cx = overlay.left + overlay.width / 2;
  const cy = overlay.top + overlay.height / 2;
  const dx = point.x - cx;
  const dy = point.y - cy;
  const localX = dx * Math.cos(rad) - dy * Math.sin(rad) + overlay.width / 2;
  const localY = dx * Math.sin(rad) + dy * Math.cos(rad) + overlay.height / 2;
  return {
    x: (localX / overlay.width) * 100,
    y: (localY / overlay.height) * 100,
  };
}

/** A pointer in `frame`'s local pixel space → PERCENT of the deskewed overlay.
 *  Exactly inverts the `rotate(angle)` the paint path applies to that overlay.
 *  Thin wrapper: `deskewedOverlayBox(frame, angle)` is centred on `frame`, so
 *  its own centre IS `frame`'s centre — the photo path's overlay and rotation
 *  angle are always the same `angle`, unlike the canvas path's tilt+user-
 *  rotation split (see `pageOverlayBox`/`flowsAtCanvasPoint`). */
export function pointInDeskewedFrame(
  point: { x: number; y: number },
  frame: Box,
  angle: number,
): { x: number; y: number } {
  return pointInRotatedOverlay(point, deskewedOverlayBox(frame, angle), angle);
}

/** A percent-space rect's pixel box on a canvas the user has rotated by
 *  `normRotation` (0/90/180/270). The single definition of that transform. */
export function rowRectInCanvas(
  row: Rect,
  canvas: { width: number; height: number },
  normRotation: number,
): Box {
  const { width: cw, height: ch } = canvas;
  if (normRotation === 90) {
    return {
      left: ((100 - row.y - row.height) / 100) * cw,
      top: (row.x / 100) * ch,
      width: (row.height / 100) * cw,
      height: (row.width / 100) * ch,
    };
  }
  if (normRotation === 180) {
    return {
      left: ((100 - row.x - row.width) / 100) * cw,
      top: ((100 - row.y - row.height) / 100) * ch,
      width: (row.width / 100) * cw,
      height: (row.height / 100) * ch,
    };
  }
  if (normRotation === 270) {
    return {
      left: (row.y / 100) * cw,
      top: ((100 - row.x - row.width) / 100) * ch,
      width: (row.height / 100) * cw,
      height: (row.width / 100) * ch,
    };
  }
  return {
    left: (row.x / 100) * cw,
    top: (row.y / 100) * ch,
    width: (row.width / 100) * cw,
    height: (row.height / 100) * ch,
  };
}

/** A canvas pixel point → percent of the UNROTATED page frame. */
export function percentInCanvas(
  point: { x: number; y: number },
  canvas: { width: number; height: number },
  normRotation: number,
): { x: number; y: number } {
  const fx = point.x / canvas.width;
  const fy = point.y / canvas.height;
  if (normRotation === 90) return { x: fy * 100, y: (1 - fx) * 100 };
  if (normRotation === 180) return { x: (1 - fx) * 100, y: (1 - fy) * 100 };
  if (normRotation === 270) return { x: (1 - fy) * 100, y: fx * 100 };
  return { x: fx * 100, y: fy * 100 };
}

// ------------------------------------------------------------- hit-testing

const angleOf = (a: number | undefined): number => a ?? 0;

/** Inclusive on all four edges — a point exactly on the seam between two
 *  merely-touching rects reports both, by design (see the seam test). */
function contains(rect: Rect, x: number, y: number): boolean {
  return (
    x >= rect.x &&
    x <= rect.x + rect.width &&
    y >= rect.y &&
    y <= rect.y + rect.height
  );
}

/**
 * Every flow whose RAW rect contains the point, in document order. Only
 * regions on the pointer's page and in its angle group are considered — the
 * caller converts the pointer into that group's frame first.
 *
 * Raw, not padded: the 0.5% paint padding would otherwise manufacture an
 * overlap at every line boundary and blink a second row down a whole sweep.
 */
export function flowsAtPoint(
  regions: SourceRegion[],
  point: { x: number; y: number; page: number; angle?: number },
): string[] {
  const ids: string[] = [];
  for (const region of regions) {
    if (region.page !== point.page) continue;
    if (angleOf(region.angle) !== angleOf(point.angle)) continue;
    if (!contains(region.rect, point.x, point.y)) continue;
    if (!ids.includes(region.flowId)) ids.push(region.flowId);
  }
  return ids;
}

/** The distinct document angles present on a page, ascending. (Not "upright
 *  first": a document's tilt comes from a circular mean over detected text
 *  lines and can be negative, which would then sort before 0. Nothing reading
 *  this list depends on 0 leading — `flowsAtCanvasPoint`/`flowsAtContentPoint`
 *  union their per-angle results order-independently, and paint groups by
 *  `Map` insertion order, not this list — so ascending is simplest and this
 *  comment now says what the code does rather than the other way round.) */
export function anglesOnPage(regions: SourceRegion[], page: number): number[] {
  const angles = new Set<number>();
  for (const region of regions) {
    if (region.page === page) angles.add(angleOf(region.angle));
  }
  return [...angles].sort((a, b) => a - b);
}

/** ONE answer for the whole page: overlapping bands mean a per-band
 *  mouseenter would answer with whichever div happens to sit on top. */
export function flowsAtCanvasPoint(
  regions: SourceRegion[],
  pointPx: { x: number; y: number },
  canvas: { width: number; height: number },
  normRotation: number,
  page: number,
): string[] {
  const ids: string[] = [];
  for (const angle of anglesOnPage(regions, page)) {
    const point = angle
      ? pointInRotatedOverlay(
          pointPx,
          pageOverlayBox(angle, canvas, normRotation),
          angle + normRotation,
        )
      : percentInCanvas(pointPx, canvas, normRotation);
    for (const id of flowsAtPoint(regions, { ...point, page, angle })) {
      if (!ids.includes(id)) ids.push(id);
    }
  }
  return ids;
}

/** Photo equivalent: the pointer arrives in the measured content box's own
 *  space, so the user's rotation is already accounted for by the DOM. */
export function flowsAtContentPoint(
  regions: SourceRegion[],
  pointPx: { x: number; y: number },
  content: { width: number; height: number },
): string[] {
  const frame: Box = { left: 0, top: 0, ...content };
  const ids: string[] = [];
  for (const angle of anglesOnPage(regions, 1)) {
    const point = angle
      ? pointInDeskewedFrame(pointPx, frame, angle)
      : {
          x: (pointPx.x / content.width) * 100,
          y: (pointPx.y / content.height) * 100,
        };
    for (const id of flowsAtPoint(regions, { ...point, page: 1, angle })) {
      if (!ids.includes(id)) ids.push(id);
    }
  }
  return ids;
}

// ------------------------------------------------------------ resting fill

function overlaps(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    b.x < a.x + a.width &&
    a.y < b.y + b.height &&
    b.y < a.y + a.height
  );
}

function boundingBox(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    width: Math.max(a.x + a.width, b.x + b.width) - x,
    height: Math.max(a.y + a.height, b.y + b.height) - y,
  };
}

/**
 * Overlapping rects merged (transitively) into their bounding box. Two reasons,
 * and the resting layer needs both — which is why fill and outline share this
 * one result rather than the outline keeping the raw per-box rects:
 *
 *  - a fill painted from the result never stacks alpha: a strip shared by two
 *    boxes must read exactly as light as a strip covered by one.
 *  - an outline painted from the result draws each BLOCK's edges only. Adjacent
 *    table lines always overlap (`PAD` inflates every pdf box by 0.5% a side),
 *    so per-box outlines rendered a ladder of doubled rules over what is
 *    visually a single region (design §2.4c).
 *
 * A rect overlapping nothing comes back unchanged, so an isolated region keeps
 * its own outline without a special case.
 */
export function mergeRects(rects: Rect[]): Rect[] {
  const merged: Rect[] = [];
  for (const rect of rects) {
    let current = rect;
    let index = 0;
    while (index < merged.length) {
      if (overlaps(current, merged[index])) {
        current = boundingBox(current, merged.splice(index, 1)[0]);
        index = 0; // the grown rect may now touch one we already passed
      } else {
        index += 1;
      }
    }
    merged.push(current);
  }
  return merged;
}

export interface RestingLayer {
  angle: number;
  overlay: Box | null;
  /** One box per merged BLOCK — the caller paints each as a single div
   *  carrying both the fill and the outline (design §2.4c). */
  boxes: Box[];
}

const percentToBox = (rect: Rect, overlay: Box): Box => ({
  left: (rect.x / 100) * overlay.width,
  top: (rect.y / 100) * overlay.height,
  width: (rect.width / 100) * overlay.width,
  height: (rect.height / 100) * overlay.height,
});

/** The neutral resting layer for one page, grouped by document angle: the
 *  page's regions merged into blocks. Upright boxes come back in canvas pixels;
 *  tilted ones in their overlay's pixels, with the overlay the caller must
 *  rotate. */
export function restingLayersForPage(
  rows: Array<Rect & { pageNumber: number; angle?: number }>,
  page: number,
  canvas: { width: number; height: number },
  normRotation: number,
): RestingLayer[] {
  const onPage = rows.filter((row) => row.pageNumber === page);

  return anglesOnPage(
    onPage.map((row) => ({ flowId: "", page, rect: row, angle: row.angle })),
    page,
  ).map((angle) => {
    const group = onPage.filter((row) => (row.angle ?? 0) === angle);
    const rects = group.map(({ x, y, width, height }) => ({
      x,
      y,
      width,
      height,
    }));
    const blocks = mergeRects(rects);
    if (!angle) {
      return {
        angle,
        overlay: null,
        boxes: blocks.map((r) => rowRectInCanvas(r, canvas, normRotation)),
      };
    }
    const placed = pageOverlayBox(angle, canvas, normRotation);
    return {
      angle,
      overlay: placed,
      boxes: blocks.map((r) => percentToBox(r, placed)),
    };
  });
}
